import Logger from './LoggerService';

interface UsercentricsService {
  id: string;
  name: string;
  categorySlug?: string;
  consent: {
    status: boolean;
    given?: boolean;
  };
}

interface UsercentricsAPI {
  getServicesBaseInfo: () => Array<UsercentricsService>;
  onConsentStatusChange?: (callback: (event: { changed: Array<{ id: string; status: boolean; name?: string; categorySlug?: string }> }) => void) => void;
  showSecondLayer: () => void;
}

declare global {
  interface Window {
    UC_UI?: UsercentricsAPI;
  }
}

export interface ConsentCheck {
  serviceName: string;
  categorySlug?: string;
}

export type ConsentCallback = (hasConsent: boolean) => void;

export class UsercentricsConsentService {
  private static listeners: Map<string, Set<ConsentCallback>> = new Map();
  private static isMonitoring = false;
  private static logger = new Logger('UsercentricsConsentService');

  public static openCookiePreferences(): void {
    if (window.UC_UI && window.UC_UI.showSecondLayer) {
      window.UC_UI.showSecondLayer();
    }
  }

  /** Check if user has given consent for a specific service or category */
  public static hasConsent(serviceName: string, categorySlug?: string): boolean {
    if (!window.UC_UI || !window.UC_UI.getServicesBaseInfo) {
      return false;
    }

    try {
      const services = window.UC_UI.getServicesBaseInfo();
      if (!services || services.length === 0) {
        return false;
      }

      return services.some(service => {
        const hasConsent = (service.consent && service.consent.status === true) ||
          (service.consent && service.consent.given === true);
        const nameMatches = service.name === serviceName;
        const categoryMatches = categorySlug ? service.categorySlug === categorySlug : true;

        return hasConsent && nameMatches && categoryMatches;
      });
    } catch (error) {
      this.logger.logWarning('Error checking consent:', error);
      return false;
    }
  }

  /** Check if user has given consent for all services in a category */
  public static hasCategoryConsent(categorySlug: string): boolean {
    if (!window.UC_UI || !window.UC_UI.getServicesBaseInfo) {
      return false;
    }

    try {
      const services = window.UC_UI.getServicesBaseInfo();
      if (!services || services.length === 0) {
        return false;
      }

      // If checking by category, we look if ANY service in this category has consent
      // or if the category itself is considered consented.
      return services.some(service => {
        const hasConsent = (service.consent && service.consent.status === true) ||
          (service.consent && service.consent.given === true);
        return hasConsent && service.categorySlug === categorySlug;
      });
    } catch (error) {
      this.logger.logWarning('Error checking category consent:', error);
      return false;
    }
  }

  /** Register a callback for consent status changes */
  public static onConsentChange(
    serviceName: string,
    categorySlug: string | undefined,
    callback: ConsentCallback
  ): () => void {
    const key = `${serviceName}:${categorySlug || '*'}`;

    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }

    this.listeners.get(key)!.add(callback);

    if (!this.isMonitoring) {
      this.startMonitoring();
    }

    const currentConsent = this.hasConsent(serviceName, categorySlug);
    this.previousConsentStates.set(key, currentConsent);
    callback(currentConsent);

    return () => {
      const callbacks = this.listeners.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }

  /** Register a callback for category-level consent changes */
  public static onCategoryConsentChange(
    categorySlug: string,
    callback: ConsentCallback
  ): () => void {
    const key = `category:${categorySlug}`;

    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }

    this.listeners.get(key)!.add(callback);

    if (!this.isMonitoring) {
      this.startMonitoring();
    }

    const currentConsent = this.hasCategoryConsent(categorySlug);
    this.previousConsentStates.set(key, currentConsent);
    callback(currentConsent);

    return () => {
      const callbacks = this.listeners.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }

  private static previousConsentStates = new Map<string, boolean>();

  /** Notify listeners when consent state changes */
  private static notifyListeners(): void {
    this.listeners.forEach((callbacks, key) => {
      let hasConsent = false;

      if (key.startsWith('category:')) {
        const categorySlug = key.split(':')[1];
        hasConsent = this.hasCategoryConsent(categorySlug);
      } else {
        const [serviceName, categorySlug] = key.split(':');
        const slug = categorySlug === '*' ? undefined : categorySlug;
        hasConsent = this.hasConsent(serviceName, slug);
      }

      const previousConsent = this.previousConsentStates.get(key);
      const previousConsentValue = previousConsent !== undefined ? previousConsent : false;

      if (hasConsent !== previousConsentValue) {
        this.previousConsentStates.set(key, hasConsent);

        callbacks.forEach(callback => {
          try {
            callback(hasConsent);
          } catch (error) {
            this.logger.logWarning('Error in consent callback:', error);
          }
        });
      }
    });
  }

  /** Monitor events and storage for Usercentrics initialization */
  private static startMonitoring(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    // Monitor various Usercentrics initialization events
    window.addEventListener('UC_UI_INITIALIZED', () => {
      UsercentricsConsentService.notifyListeners();
    });

    window.addEventListener('ucEvent', (event: any) => {
      if (event.detail && (event.detail.type === 'consent_status' || event.detail.event === 'consent_status' || event.detail.type === 'initial_consent_status')) {
        UsercentricsConsentService.notifyListeners();
      }
    });

    // Safety polling to catch late initializations
    setInterval(() => {
      UsercentricsConsentService.notifyListeners();
    }, 1000);

    // Intercept storage changes as a reactive trigger
    if (typeof Storage !== 'undefined' && Storage.prototype) {
      const originalSetItem = Storage.prototype.setItem;
      Storage.prototype.setItem = function (key: string, value: string) {
        originalSetItem.call(this, key, value);

        if (key === 'uc_settings') {
          UsercentricsConsentService.notifyListeners();
        }
      };
    }
  }
}
